import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Heart } from "lucide-react";

// Colour pairs so unlisted shows don't all look the same. Picked from the name, so a show always gets the same one.
const PALETTES = [
  ["#7a1020", "#2a0a12"],
  ["#4a2a6a", "#160d22"],
  ["#1f4a5a", "#0a1a20"],
  ["#6a4a12", "#1f1608"],
];

function initials(name) {
  const words = (name || "").replace(/[^\p{L}\p{N}\s]/gu, " ").split(/\s+/).filter(Boolean);
  const skip = new Set(["the", "a", "an", "of", "and"]);
  const main = words.filter((w) => !skip.has(w.toLowerCase()));
  const use = (main.length ? main : words).slice(0, 2);
  return use.map((w) => w[0]).join("").toUpperCase() || "?";
}

function hash(s) {
  let h = 0;
  for (const c of s || "") h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return h;
}

/** Poster image, or a themed placeholder when the show has none. Size comes from the parent (fills it). */
export function DiaryPoster({ src, name }) {
  const [broken, setBroken] = useState(false);
  if (src && !broken) {
    return <img src={src} alt="" onError={() => setBroken(true)} className="h-full w-full object-cover" />;
  }
  const [a, b] = PALETTES[hash(name) % PALETTES.length];
  return (
    <div data-testid="poster-placeholder" aria-hidden="true"
      className="relative flex h-full w-full items-center justify-center overflow-hidden"
      style={{ background: `linear-gradient(160deg, ${a}, ${b})` }}>
      <div className="absolute inset-1.5 rounded-md border border-[#C89B3C]/50" />
      <span className="font-serif text-2xl font-bold tracking-wide text-[hsl(var(--gold))]">{initials(name)}</span>
    </div>
  );
}

/** "2026-03-05" -> { day: "5", month: "MAR", year: "2026" } without any timezone shifting. */
export function dateParts(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || "");
  if (!m) return { day: "", month: "", year: "" };
  const months = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
  return { day: String(Number(m[3])), month: months[Number(m[2]) - 1] || "", year: m[1] };
}

const ACCENTS = ["#c4283c", "#8a5cc4", "#3a9ab5", "#d4a32c"];
/** Brighter version of the placeholder colour, used as the edge of each diary card. */
export function showAccent(name) {
  return ACCENTS[hash(name) % ACCENTS.length];
}

export function ratingLabel(r) {
  return { 5: "Loved it", 4: "Really good", 3: "Good", 2: "Okay", 1: "Not for me" }[r] || "";
}

const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/** Entries (already newest first) -> [{ key: "2026-03", label: "March 2026", items }] */
export function groupByMonth(entries) {
  const out = [];
  for (const e of entries) {
    const key = (e.date || "").slice(0, 7);
    let g = out[out.length - 1];
    if (!g || g.key !== key) {
      const [y, m] = key.split("-");
      g = { key, label: `${MONTH_NAMES[Number(m) - 1] || ""} ${y}`.trim(), items: [] };
      out.push(g);
    }
    g.items.push(e);
  }
  return out;
}

/** Fun facts from a list of entries: top-rated show, favourite theatre, shows this year. */
export function computeHighlights(entries, year) {
  const rated = entries.filter((e) => e.rating);
  let top = null;
  for (const e of rated) if (!top || e.rating > top.rating) top = e; // newest wins ties (list is newest first)
  const counts = {};
  for (const e of entries) {
    const v = (e.venue || "").trim();
    if (v) counts[v] = (counts[v] || 0) + 1;
  }
  const venues = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  const fav = venues.length && venues[0][1] > 1 ? { name: venues[0][0], visits: venues[0][1] } : null;
  const thisYear = entries.filter((e) => (e.date || "").startsWith(String(year))).length;
  return { top: top ? { name: top.show_name, rating: top.rating } : null, fav, thisYear };
}

/** Big bold title card shared by the private diary and the public page. */
export function DiaryHeader({ title, stats, kicker = "Theatre diary" }) {
  const bits = [
    `🎟️ ${stats.shows ?? stats.total} show${(stats.shows ?? stats.total) === 1 ? "" : "s"}`,
    stats.this_year != null ? `📅 ${stats.this_year} this year` : null,
    stats.average_rating ? `⭐ ${stats.average_rating} average` : null,
    stats.theatres ? `🏛️ ${stats.theatres} theatre${stats.theatres === 1 ? "" : "s"}` : null,
  ].filter(Boolean);
  return (
    <div data-testid="diary-header" className="relative overflow-hidden rounded-3xl border border-[#C89B3C]/40 bg-gradient-to-br from-[#7a1020] via-[#2a1620] to-[#0D0A0E] px-6 py-8 text-center sm:px-10 sm:py-10">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-transparent via-[#C89B3C] to-transparent" />
      <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#E6C071]">🎭 {kicker}</p>
      <h1 className="mt-3 break-words font-serif text-3xl font-bold leading-tight text-amber-50 sm:text-5xl" data-testid="public-title">{title}</h1>
      <div className="mx-auto mt-4 h-px w-24 bg-[#C89B3C]/70" />
      <p className="mt-4 text-sm text-amber-100/80 sm:text-base" data-testid="diary-summary">{bits.join("   ·   ")}</p>
    </div>
  );
}

export function MonthRibbon({ label, count }) {
  return (
    <div className="flex items-center gap-3" data-testid="month-ribbon">
      <span className="rounded-r-full bg-gradient-to-r from-[#7a1020] to-[#9E1B28] py-1 pl-4 pr-5 font-serif text-sm font-semibold text-amber-50 shadow">{label}</span>
      <span className="h-px flex-1 bg-gradient-to-r from-[#C89B3C]/40 to-transparent" />
      <span className="text-xs text-muted-foreground">{count} show{count === 1 ? "" : "s"}</span>
    </div>
  );
}

export function Highlights({ h }) {
  const cards = [
    h.top && ["⭐", "Top rated", h.top.name],
    h.fav && ["🏛️", "Favourite theatre", `${h.fav.name} · ${h.fav.visits} visits`],
    ["📅", "Shows this year", String(h.thisYear)],
  ].filter(Boolean);
  return (
    <div className="grid gap-3 sm:grid-cols-3" data-testid="highlights">
      {cards.map(([emoji, label, value]) => (
        <div key={label} className="rounded-2xl border border-[#C89B3C]/25 bg-card p-4">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">{emoji} {label}</p>
          <p className="mt-1 font-serif text-lg font-semibold leading-snug text-[hsl(var(--gold))]">{value}</p>
        </div>
      ))}
    </div>
  );
}

export const REACTIONS = [
  ["heart", "❤️", "Love this"],
  ["seen_too", "👀", "I've seen this too"],
  ["want_to_see", "🎟️", "On my list"],
];

/** Reaction buttons for a friend's entry. `entry` carries reactions (counts) and mine (my reaction types).
 *  Pass canReact={false} for your own entries: counts only. onChange gets the updated entry. */
/** The heart: a clear round button with a heart icon and the number of likes. Filled when you have liked it. */
export function HeartButton({ on, n = 0, canReact = true, busy = false, onClick, testid, label = "Like" }) {
  return (
    <button type="button" disabled={!canReact || busy} onClick={onClick} aria-pressed={on} aria-label={on ? "Unlike" : label} data-testid={testid} title={label}
      className={`inline-flex h-10 items-center gap-1.5 rounded-full border px-3.5 text-sm font-semibold transition-colors ${on ? "border-[#E5484D] bg-[#E5484D]/15 text-[#FF8A8E]" : "border-border text-muted-foreground"} ${canReact ? "hover:border-[#E5484D]/70" : "cursor-default"}`}>
      <Heart className={`h-5 w-5 ${on ? "fill-[#E5484D] text-[#E5484D]" : ""}`} aria-hidden="true" />
      {n > 0 && <span data-testid={`${testid}-count`}>{n}</span>}
    </button>
  );
}

export function ReactionBar({ entry, canReact = true, onChange }) {
  const [busy, setBusy] = useState(false);
  const counts = entry.reactions || {};
  const mine = entry.mine || [];
  const toggle = async (type) => {
    if (!canReact || busy) return;
    const on = !mine.includes(type);
    setBusy(true);
    try {
      await api.put(`/diary/${entry.id}/reaction`, { type, on });
      onChange({
        ...entry,
        mine: on ? [...mine, type] : mine.filter((t) => t !== type),
        reactions: { ...counts, [type]: Math.max(0, (counts[type] || 0) + (on ? 1 : -1)) },
      });
    } catch {
      toast.error("Could not save that reaction. Please try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-3 flex flex-wrap gap-2" data-testid="reaction-bar">
      {REACTIONS.map(([type, emoji, label]) => {
        const n = counts[type] || 0;
        const on = mine.includes(type);
        if (!canReact && n === 0) return null;
        if (type === "heart") return <HeartButton key={type} on={on} n={n} canReact={canReact} busy={busy} onClick={() => toggle(type)} testid="react-heart" label={label} />;
        return (
          <button key={type} type="button" disabled={!canReact || busy} onClick={() => toggle(type)} aria-pressed={on}
            data-testid={`react-${type}`} title={label}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors ${on ? "border-[#C89B3C] bg-[#C89B3C]/20 text-foreground" : "border-border text-muted-foreground"} ${canReact ? "hover:border-[#C89B3C]/60" : "cursor-default"}`}>
            <span aria-hidden="true">{emoji}</span> {label}{n > 0 && <span className="font-semibold text-[hsl(var(--gold))]">{n}</span>}
          </button>
        );
      })}
    </div>
  );
}

export const TAGS = ["Funny", "Moving", "Spectacular", "Great music", "Clever", "Thought-provoking", "Dark",
  "Cried", "Good for first-timers", "Family friendly", "Date night", "Not for everyone"];

/** The words part of an entry: verdict, tags, thoughts, review (expandable), best performance, who it's for.
 *  Works for your own entries and for friends'/public ones (those only carry these when "Include my thoughts" was ticked). */
export function EntryExtras({ e }) {
  const [open, setOpen] = useState(false);
  const long = (e.review || "").length > 280;
  const review = e.review && !open && long ? `${e.review.slice(0, 280).trimEnd()}…` : e.review;
  return (
    <>
      {e.verdict && <p className="mt-2 font-serif text-base italic text-foreground" data-testid="verdict">“{e.verdict}”</p>}
      {e.tags?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="entry-tags">
          {e.tags.map((t) => <span key={t} className="rounded-full border border-[#C89B3C]/40 bg-[#C89B3C]/10 px-2.5 py-0.5 text-xs text-[hsl(var(--gold))]">{t}</span>)}
        </div>
      )}
      {e.note && <p className="mt-2 text-sm leading-relaxed text-foreground/85">💬 {e.note}</p>}
      {e.review && (
        <div className="mt-2" data-testid="entry-review">
          <p className="whitespace-pre-line text-sm leading-relaxed text-foreground/85">{review}</p>
          {long && <button type="button" onClick={() => setOpen((o) => !o)} className="mt-1 text-xs text-[#C89B3C] hover:underline">{open ? "Show less" : "Read the full review"}</button>}
        </div>
      )}
      {e.best_performance && <p className="mt-2 text-sm text-foreground/85">🌟 <span className="text-muted-foreground">Best performance:</span> {e.best_performance}</p>}
      {e.recommend_for && <p className="mt-1 text-sm text-foreground/85">👥 <span className="text-muted-foreground">Great for:</span> {e.recommend_for}</p>}
    </>
  );
}